import ActionTypes from './ActionTypes';
import { ActionType as AccessTokenActionType, Action as AccessTokenAction } from '~/AccessToken';
import { Pagination } from '~/Api/Pagination';
import { ProductHistoryEvent } from '~/Api/Product/History';

export interface EventsState {
	request: Promise<any>;
	isFetching: boolean;
	events: ProductHistoryEvent[];
	pagination: Pagination | null;
}

export interface State {
	byProductId: { [key: string]: EventsState };
}

type Action =
	| { type: ActionTypes.request; request: Promise<any>; productId: string }
	| {
			type: ActionTypes.receive;
			request: Promise<any>;
			productId: string;
			events: ProductHistoryEvent[];
			pagination: Pagination;
	  }
	| {
			type: ActionTypes.receive;
			request: Promise<any>;
			productId: string;
			events: null;
			pagination: null;
	  }
	| { type: ActionTypes.prune; productId: string };

function createState(): State {
	return {
		byProductId: {},
	};
}

export default function reducer(state: State = createState(), action: Action | AccessTokenAction): State {
	switch (action.type) {
		case ActionTypes.request: {
			const eventsState = state.byProductId[action.productId];

			return {
				...state,
				byProductId: {
					...state.byProductId,
					[action.productId]: {
						request: action.request,
						isFetching: true,
						events: eventsState?.events ?? [],
						pagination: eventsState?.pagination ?? null,
					},
				},
			};
		}

		case ActionTypes.receive: {
			const eventsState = state.byProductId[action.productId];
			if (eventsState?.request !== action.request) {
				return state;
			}

			if (!action.events) {
				const byProductId = { ...state.byProductId };
				delete byProductId[action.productId];
				return { ...state, byProductId };
			}

			const { events, pagination } = action;
			const newEvents = [...eventsState.events];
			newEvents.splice(pagination.getFirstIndex(), pagination.perPage, ...events);

			return {
				...state,
				byProductId: {
					...state.byProductId,
					[action.productId]: {
						...eventsState,
						isFetching: false,
						events: newEvents,
						pagination,
					},
				},
			};
		}

		case ActionTypes.prune: {
			const byProductId = { ...state.byProductId };
			delete byProductId[action.productId];
			return { ...state, byProductId };
		}

		case AccessTokenActionType.receive:
			return action.userChanged ? createState() : state;

		case AccessTokenActionType.receiveLogin:
			return createState();

		default:
			return state;
	}
}

export function selectEventsByProductId(state: State, productId: string): EventsState | null {
	return state.byProductId[productId] ?? null;
}
